/**
 * Composant - DepositPage (nouvelle version Clean Architecture)
 *
 * Page de versement d'espèces avec workflow simplifié:
 * 1. Saisie du numéro de compte → recherche CBS (sans solde)
 * 2. Affichage infos client → confirmation
 * 3. Billetage → versement → reçu
 */

import { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Banknote, Check, FileCheck, Search } from 'lucide-react';
import '../withdrawal/WorkflowProgress.css';
import '../withdrawal/etapes.css';
import { useDeposit } from '../../hooks/useDeposit';
import { useDependencies } from '../../contexts/DependencyContext';
import { useAuth } from '../../contexts/AuthContext';
import { AccountSearchStep } from './AccountSearchStep';
import { DepositFormStep } from './DepositFormStep';
import { TransactionReceipt } from '../common/TransactionReceipt';
import { Account, AccountType, AccountStatus } from '../../../domain/entities/Account';
import { AccountNumber } from '../../../domain/value-objects/AccountNumber';
import { CbsAccountInfo } from '../../../application/ports/output/ICbsAccountLookup';

type DepositStep = 'search' | 'form' | 'success';

export function DepositPage() {
  const { cbsAccountLookup } = useDependencies();
  const { executeDeposit, loading, error, result, reset } = useDeposit();
  const { user } = useAuth();

  const [step, setStep] = useState<DepositStep>('search');
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [cbsInfo, setCbsInfo] = useState<CbsAccountInfo | null>(null);

  // Récupérer le code de l'utilisateur connecté
  const userCode = user?.cardData?.code || user?.username || '';

  /**
   * Recherche un compte via le CBS (appelé par AccountSearchStep)
   */
  const handleCbsSearch = useCallback(async (numcpt: string): Promise<CbsAccountInfo | null> => {
    // Passer le code de l'utilisateur connecté au lieu de VITE_AGENT_CODE
    return cbsAccountLookup.searchAccount(numcpt, userCode);
  }, [cbsAccountLookup, userCode]);

  /**
   * Quand le caissier confirme le compte trouvé, on crée un Account synthétique
   * à partir des données CBS pour alimenter le DepositFormStep
   */
  const handleAccountConfirmed = (info: CbsAccountInfo) => {
    setCbsInfo(info);

    // Créer un Account synthétique depuis les données CBS
    const account = Account.reconstitute({
      accountNumber: AccountNumber.create(info.numcpt),
      type: AccountType.CURRENT,
      status: AccountStatus.ACTIVE,
      holders: [
        {
          id: info.codeuser,
          name: info.intitule,
          status: 'active' as const,
          isPrimary: true,
        },
      ],
      createdAt: new Date(),
      currency: 'XOF',
    });

    setSelectedAccount(account);
    setStep('form');
  };

  const handleBackToSearch = () => {
    setSelectedAccount(null);
    setCbsInfo(null);
    setStep('search');
    reset();
  };

  const handleDepositSubmit = async (data: {
    denomination: Map<number, number>;
    depositorInfo: any;
    notes?: string;
  }) => {
    if (!selectedAccount) return;

    console.log('[DepositPage] Début du versement...', {
      account: selectedAccount.accountNumber.value,
      codeuser: cbsInfo?.codeuser,
      denomination: Object.fromEntries(data.denomination.entries()),
    });

    await executeDeposit(
      {
        accountNumber: selectedAccount.accountNumber.value,
        denomination: Object.fromEntries(data.denomination.entries()),
        depositorInfo: data.depositorInfo,
        notes: data.notes,
        cashierId: userCode,
        cashierName: user?.displayName || 'Agent Principal',
      },
      selectedAccount  // Passer le compte déjà résolu pour éviter un re-fetch CBS
    );
  };

  // Gestion de l'état de succès - transition directe quand result change
  useEffect(() => {
    if (result && step === 'form') {
      console.log('[DepositPage] Versement réussi:', result);
      setStep('success');
    }
  }, [result, step]);

  useEffect(() => {
    if (error) {
      console.log('[DepositPage] Erreur:', error);
    }
  }, [error]);

  const handleNewDeposit = () => {
    setSelectedAccount(null);
    setCbsInfo(null);
    setStep('search');
    reset();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <EtapesVersement etape={step} />

      {/* Étape 1: Recherche du compte via CBS */}
      {step === 'search' && (
        <AccountSearchStep
          onAccountConfirmed={handleAccountConfirmed}
          onSearch={handleCbsSearch}
          loading={loading}
        />
      )}

      {/* Étape 2: Billetage */}
      {step === 'form' && selectedAccount && (
        <div className="et et-large">
          <button type="button" onClick={handleBackToSearch} disabled={loading} className="et-retour">
            <ArrowLeft />
            Changer de compte
          </button>
          <DepositFormStep
            account={selectedAccount}
            onSubmit={handleDepositSubmit}
            onCancel={handleBackToSearch}
            loading={loading}
            error={error}
          />
        </div>
      )}

      {/* Étape 3: Reçu */}
      {step === 'success' && result && (
        <TransactionReceipt transaction={result} onClose={handleNewDeposit} />
      )}
    </div>
  );
}

const ETAPES: { id: DepositStep; label: string; description: string; icon: typeof Search }[] = [
  { id: 'search', label: 'Compte', description: 'Recherche au CBS', icon: Search },
  { id: 'form', label: 'Billetage', description: 'Coupures remises', icon: Banknote },
  { id: 'success', label: 'Reçu', description: 'Remise au déposant', icon: FileCheck },
];

/** Barre d'étapes du versement (styles partagés avec le retrait : WorkflowProgress.css) */
function EtapesVersement({ etape }: { etape: DepositStep }) {
  const courante = ETAPES.findIndex((e) => e.id === etape);
  return (
    <nav aria-label="Étapes du versement" className="wp">
      <ol>
        {ETAPES.map((e, index) => {
          const statut = index < courante || etape === 'success' ? 'completed' : index === courante ? 'active' : 'pending';
          const Icon = e.icon;
          const dernier = index === ETAPES.length - 1;
          return (
            <li key={e.id} className={dernier ? '' : 'wp-flex'} aria-current={statut === 'active' ? 'step' : undefined}>
              <div className="wp-step">
                <span className={`wp-pastille ${statut}`}>{statut === 'completed' ? <Check strokeWidth={2.5} /> : <Icon />}</span>
                <span className="wp-textes">
                  <span className={`wp-label ${statut}`}>{e.label}</span>
                  <span className="wp-desc">{e.description}</span>
                </span>
              </div>
              {!dernier && <span aria-hidden="true" className={`wp-trait ${index < courante ? 'fait' : ''}`} />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
